import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { useAppDispatch } from "src/store";
import { getSchemaCategories, mapAssetGroup } from "src/store/ontology/saga";
import { SchemaCategory, SchemaSector, UnmappedGroup } from "src/store/ontology/type";

interface Props {
  group: UnmappedGroup;
  sectors: SchemaSector[];
  open: boolean;
  onHide: () => void;
  onMapped: () => void;
}

/**
 * Tenant kategorisini ontolojiye bağlama.
 *
 * Bu işlem doğrudan görünürlük değiştirir: eşlenen kategorideki ürünler
 * aramaya çıkar. Bu yüzden kaç ürünü etkileyeceği açıkça yazılıyor.
 */
const MapGroupModal = ({ group, sectors, open, onHide, onMapped }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [sectorRef, setSectorRef] = useState("");
  const [categories, setCategories] = useState<SchemaCategory[]>([]);
  const [taxonomyId, setTaxonomyId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!sectorRef) {
      setCategories([]);
      setTaxonomyId("");
      return;
    }
    dispatch(
      getSchemaCategories({
        id: sectorRef,
        payload: {
          onSuccess: (_m: any, p: any) => setCategories(p?.Categories ?? []),
          onError: () => setCategories([]),
        },
      })
    );
  }, [dispatch, sectorRef]);

  const submit = () => {
    if (!taxonomyId) return;
    setBusy(true);
    setError(undefined);
    dispatch(
      mapAssetGroup({
        body: { AssetGroupId: group.Id, TaxonomyId: taxonomyId },
        payload: {
          onSuccess: () => {
            setBusy(false);
            onMapped();
          },
          onError: (m: string) => {
            setBusy(false);
            setError(m);
          },
        },
      })
    );
  };

  return (
    <Modal isOpen={open} toggle={onHide} centered>
      <ModalHeader toggle={onHide}>
        {t("Ontology.MapTitle", "Kategoriyi ontolojiye bağla")}
      </ModalHeader>
      <ModalBody>
        {error && <div className="alert alert-danger py-2">{error}</div>}

        <p className="mb-3">
          <strong>{group.Name}</strong>{" "}
          <span className="text-muted">({group.ClientName})</span>
          <br />
          <span className="text-muted" style={{ fontSize: 13 }}>
            {t("Ontology.MapEffect", "Bu işlem sonrası aramaya çıkacak ürün sayısı")}:{" "}
            <strong>{group.CardCount}</strong>
          </span>
        </p>

        <div className="mb-3">
          <label className="form-label">{t("Ontology.Sector", "Sektör")}</label>
          <select className="form-select" value={sectorRef} onChange={(e) => setSectorRef(e.target.value)}>
            <option value="">{t("Components.Select", "Seçiniz")}</option>
            {sectors.map((s) => (
              <option key={s.ParentSector} value={s.ParentSector}>
                {s.Name} ({s.ParentSector})
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">{t("Ontology.Category", "Kategori")}</label>
          <select
            className="form-select"
            value={taxonomyId}
            disabled={!sectorRef}
            onChange={(e) => setTaxonomyId(e.target.value)}
          >
            <option value="">{t("Components.Select", "Seçiniz")}</option>
            {categories.map((c) => (
              <option key={c.TaxonomyId} value={c.TaxonomyId}>
                {c.Name} ({c.TaxonomyId})
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-secondary" onClick={onHide}>
            {t("Components.Cancel", "Vazgeç")}
          </button>
          <button className="btn btn-primary" disabled={!taxonomyId || busy} onClick={submit}>
            {busy ? t("Components.Saving", "Kaydediliyor…") : t("Ontology.MapButton", "Eşle")}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default MapGroupModal;
